// @flow
// -----------------------------------------------------------------
// <copyright
//   file="Izenda.ConnectionStringBuilderEndpoint.js"
//   company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// -----------------------------------------------------------------

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { action } from 'mobx';
import classnames from 'classnames';
import { isEmpty, find, isNil, partial, getOr } from 'lodash/fp';

import connect from 'share/enhancers/Izenda.Connect';
import localizer from 'share/localization/Izenda.Localizer';
import RequestBuilderHeader from './Izenda.RequestBuilderHeaders';
import RequestBuilderBody from './Izenda.RequestBuilderBody';
import Panel from 'share/components/controls/Izenda.Panel';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import InputByDataType from 'share/components/controls/Izenda.InputByDataType';
import Button from 'share/components/controls/Izenda.Button';
import EventsUtils from 'share/utils/Izenda.EventUtils';
import Validator from 'share/components/hoc/Izenda.Validator';
import FormGroup from 'share/components/controls/Izenda.FormGroup';
import Label from 'share/components/controls/Izenda.Label';
import ConnectionRestEndpoint from 'logic/models/Izenda.ConnectionRestEndpoint';

const InputByDataTypeValidator = Validator()(InputByDataType);

/**
 * Connection string builder endpoint editor component
 */
@connect()
export default class ConnectionStringBuilderEndpoint extends Component {
  static propTypes = {
    endpointModel: PropTypes.any.isRequired,
    cachingConfiguration: PropTypes.any.isRequired
  };

  state = {
    isAdvancedVisible: false
  };

  /**
   * Get connection string parts UI
   */
  getConnectionStringPartsUi = () => {
    const isEnableDataCache = getOr(false, 'cachingConfiguration.dataCachingConfiguration.isEnableDataCache', this.props.cachingConfiguration);
    return [
      {
        label: localizer.getText('CONNECTION_STRING_BUILDER_REST_ENDPOINT_NAME'),
        hint: localizer.getText('CONNECTION_STRING_BUILDER_REST_ENDPOINT_NAME_HINT'),
        name: 'endpointName',
        type: 'text',
        validation: {
          presence: {
            presence: true
          },
          format: {
            pattern: /[a-zA-Z0-9 _]+/
          }
        }
      }, {
        label: localizer.getText('CONNECTION_STRING_BUILDER_REST_LABEL_ADDRESS'),
        hint: localizer.getText('CONNECTION_STRING_BUILDER_REST_LABEL_ADDRESS_HINT'),
        name: 'address',
        type: 'text',
        validation: { presence: { presence: true } }
      }, {
        label: localizer.getText('CONNECTION_STRING_BUILDER_REST_LABEL_SCHEMEPATH'),
        hint: localizer.getText('CONNECTION_STRING_BUILDER_REST_LABEL_SCHEMEPATH_HINT'),
        name: 'schemePath',
        type: 'text',
        validation: {}
      }, {
        label: localizer.getText('CONNECTION_STRING_BUILDER_REST_LABEL_DATAPATH'),
        hint: localizer.getText('CONNECTION_STRING_BUILDER_REST_LABEL_DATAPATH_HINT'),
        name: 'dataPath',
        type: 'text',
        validation: { presence: { presence: true } }
      }, {
        label: localizer.getText('CONNECTION_STRING_BUILDER_REST_LABEL_FLATTENARRAYS'),
        name: 'flattenArrays',
        type: 'checkbox',
        validation: {}
      }, {
        label: localizer.getText('CONNECTION_STRING_BUILDER_REST_LABEL_FLATTENOBJECTS'),
        name: 'flattenObjects',
        type: 'checkbox',
        validation: {}
      }, {
        label: localizer.getText('CONNECTION_STRING_BUILDER_REST_LABEL_FLATTENDEPTH'),
        name: 'flattenDepth',
        type: 'number',
        validation: {}
      }, {
        label: localizer.getText('CONNECTION_STRING_BUILDER_REST_LABEL_SEPARATOR'),
        name: 'subColumnSeparator',
        type: 'text',
        validation: {}
      }, {
        label: localizer.getText('CONNECTION_STRING_BUILDER_REST_LABEL_CACHE_ENABLED'),
        name: 'requestCacheEnabled',
        type: 'checkbox',
        disabled: !isEnableDataCache,
        validation: {}
      }, {
        label: localizer.getText('CONNECTION_STRING_BUILDER_REST_LABEL_CACHE_TTL'),
        name: 'requestCacheTtl',
        type: 'number',
        disabled: !isEnableDataCache,
        validation: {}
      }
    ];
  };

  /**
   * Connection string part changed handler
   * @param {string} propertyName property which was changed
   * @param {any} newVal New value
   */
  @action onChangeConnectionStringPart = (propertyName, newVal) => {
    const endpointModel: ConnectionRestEndpoint = this.props.endpointModel;
    endpointModel[propertyName] = newVal;
  };

  /**
   * Render single connection part item
   * @param {string} name Connection part name
   * @returns {*}
   */
  renderConnectionStringFormItem = name => {
    const endpointModel: ConnectionRestEndpoint = this.props.endpointModel;
    const connectionStringPartUi = find(x => x.name === name, this.getConnectionStringPartsUi());
    if (isNil(connectionStringPartUi)) {
      throw `Unknown "${name}" connection part item`;
    }
    const onChangeHandler = partial(this.onChangeConnectionStringPart, [name]);

    const labelClasses = classnames('validation control-label', {
      'required': !isEmpty(connectionStringPartUi.validation)
    });

    const value = endpointModel[name];

    return (
      <FormGroup className="u_fullWidth">
        <Label className={labelClasses}>{connectionStringPartUi.label}</Label>
        {!isEmpty(connectionStringPartUi.hint) && (
          <a className='icon-info-circled u_alignMiddle' title={connectionStringPartUi.hint}/>
        )}
        <InputByDataTypeValidator
          type={connectionStringPartUi.type}
          value={value}
          disabled={connectionStringPartUi.disabled}
          validations={connectionStringPartUi.validation}
          field={connectionStringPartUi.name}
          onChange={EventsUtils.notifyChange(onChangeHandler, EventsUtils.INPUT_BY_TYPE)}
        />
      </FormGroup>
    );
  };

  /**
   * Render connection string parameters form.
   */
  renderConnectionStringForm = () => {
    const { isAdvancedVisible } = this.state;
    return (
      <Panel className="panel-default">
        <GridLayout>
          <GridLayout.Row>
            <GridLayout.Col cols="6">
              {this.renderConnectionStringFormItem('endpointName')}
            </GridLayout.Col>
            <GridLayout.Col cols="6">
              {this.renderConnectionStringFormItem('address')}
            </GridLayout.Col>
          </GridLayout.Row>
          <GridLayout.Row>
            <GridLayout.Col cols="6">
              {this.renderConnectionStringFormItem('schemePath')}
            </GridLayout.Col>
            <GridLayout.Col cols="6">
              {this.renderConnectionStringFormItem('dataPath')}
            </GridLayout.Col>
          </GridLayout.Row>

          {/*Advanced options button*/}
          {!isAdvancedVisible && (
            <GridLayout.Row>
              <GridLayout.Col cols="12">
                <Button
                  label={localizer.getText('CONNECTION_ADVANCED_OPTIONS')}
                  onClick={() => this.setState({ isAdvancedVisible: true })}
                />
              </GridLayout.Col>
            </GridLayout.Row>
          )}

          {/*Advanced panel*/}
          {isAdvancedVisible && (
            <GridLayout.Row>
              <GridLayout.Col cols="12">
                {localizer.getText('CONNECTION_ADVANCED_OPTIONS')}
                &nbsp;&nbsp;
                {'('}
                <a
                  className="underline"
                  onClick={e => {
                    e.preventDefault();
                    this.setState({ isAdvancedVisible: false })
                  }}>
                  {localizer.getText('CONNECTION_ADVANCED_OPTIONS_SHOW_LESS')}
                </a>
                {')'}
              </GridLayout.Col>
            </GridLayout.Row>
          )}
          {isAdvancedVisible && (
            <GridLayout.Row>
              <GridLayout.Col cols="2">
                {this.renderConnectionStringFormItem('flattenArrays')}
              </GridLayout.Col>
              <GridLayout.Col cols="2">
                {this.renderConnectionStringFormItem('flattenObjects')}
              </GridLayout.Col>
              <GridLayout.Col cols="4">
                {this.renderConnectionStringFormItem('flattenDepth')}
              </GridLayout.Col>
              <GridLayout.Col cols="4">
                {this.renderConnectionStringFormItem('subColumnSeparator')}
              </GridLayout.Col>
            </GridLayout.Row>
          )}
          {isAdvancedVisible && (
            <GridLayout.Row>
              <GridLayout.Col cols="12">
                {localizer.getText('CONNECTION_STRING_BUILDER_REST_LABEL_CACHE_SETTINGS')}
              </GridLayout.Col>
            </GridLayout.Row>
          )}
          {isAdvancedVisible && (
            <GridLayout.Row>
              <GridLayout.Col cols="4">
                {this.renderConnectionStringFormItem('requestCacheEnabled')}
              </GridLayout.Col>
              <GridLayout.Col cols="4">
                {this.renderConnectionStringFormItem('requestCacheTtl')}
              </GridLayout.Col>
            </GridLayout.Row>
          )}
        </GridLayout>
      </Panel>
    );
  };

  render() {
    const endpointModel: ConnectionRestEndpoint = this.props.endpointModel;

    return (
      <div className="connection-string-builder-tabs-body">
        {/*Render connection string parts*/}
        {this.renderConnectionStringForm()}

        {/*Render request params*/}
        <RequestBuilderHeader request={endpointModel.request}/>
        <RequestBuilderBody request={endpointModel.request}/>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/ConnectionStringBuilderRest/Izenda.ConnectionStringBuilderEndpoint.js